import { useCallback, useEffect, useRef, useState } from 'react'
import { nearestCentroid, isUsablePosition, type NearbyCentroid, type NearbyMatch } from '../../../nearby/centroids'

/**
 * The Nearby row's state machine (#861, toolkit: Searchbar Pro `enable_nearby`).
 *
 * Geolocation is requested ONLY from the click, never on mount: a widget that
 * asks for a visitor's position on page load gets denied once and stays denied
 * for the origin. The toolkit does the same (`handleNearbyClick`).
 *
 * Every failure the toolkit collapses into one message stays collapsed here
 * (denied / unavailable / timeout all read "Couldn't get your location") — a
 * visitor cannot act on the difference, and naming the denial would only
 * pressure them about a permission they already refused.
 *
 * The one deliberate DIVERGENCE from the toolkit: it calls
 * `getCurrentPosition(ok, err)` with no options, so a device that never
 * resolves a fix leaves the row on "Loading…" forever. This passes a timeout.
 */
export type NearbyStatus = 'idle' | 'locating' | 'error' | 'empty' | 'unsupported'

/** Long enough for a cold GPS fix, short enough that a hung provider still
 *  ends in a message the visitor can act on. */
const GEO_TIMEOUT_MS = 10_000
/** A position fixed within the last five minutes is good enough to pick a
 *  destination hundreds of km wide, and reusing it skips a second prompt-free
 *  hardware wake on a second click. */
const GEO_MAX_AGE_MS = 300_000

export function useNearby(
  centroids: NearbyCentroid[],
  ownedKeys: readonly string[],
  onMatch: (match: NearbyMatch) => void,
) {
  const [status, setStatus] = useState<NearbyStatus>('idle')
  // A resolve that lands after unmount must not setState behind React's back.
  // Set on SETUP as well as cleanup: StrictMode runs setup -> cleanup -> setup,
  // and a cleanup-only ref would leave the hook permanently inert in dev.
  const liveRef = useRef(true)
  useEffect(() => {
    liveRef.current = true
    return () => { liveRef.current = false }
  }, [])
  // Generation token. Geolocation has no AbortController, so a request that is
  // still outstanding when the visitor gives up and picks a destination by hand
  // would otherwise resolve later and REPLACE their choice. Every reset (which
  // the field fires on close, and therefore on any manual pick) invalidates
  // whatever is in flight.
  const genRef = useRef(0)

  const resolve = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatus('unsupported')
      return
    }
    setStatus('locating')
    const gen = ++genRef.current
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!liveRef.current || gen !== genRef.current) return
        const { latitude, longitude } = pos.coords
        // The toolkit validates range and the (0,0) sentinel server-side; with
        // no server in this port the check lives here, so a device reporting
        // (0,0) reads as a failed fix rather than a visitor off West Africa.
        if (!isUsablePosition(latitude, longitude)) {
          setStatus('error')
          return
        }
        const match = nearestCentroid(centroids, { lat: latitude, lng: longitude }, ownedKeys)
        if (!match) {
          setStatus('empty')
          return
        }
        setStatus('idle')
        onMatch(match)
      },
      () => {
        if (!liveRef.current || gen !== genRef.current) return
        setStatus('error')
      },
      { timeout: GEO_TIMEOUT_MS, maximumAge: GEO_MAX_AGE_MS },
    )
  }, [centroids, ownedKeys, onMatch])

  /** Re-opening the field after a failure must offer a fresh try, not the stale
   *  error the visitor already read — and anything still in flight from the
   *  previous open must not land on top of what the visitor did next. */
  const reset = useCallback(() => {
    genRef.current += 1
    setStatus('idle')
  }, [])

  return { status, resolve, reset }
}

export const NEARBY_COPY: Record<NearbyStatus, string> = {
  idle: "Find what's around you",
  locating: 'Loading…',
  error: "Couldn't get your location",
  empty: 'No nearby destinations available yet',
  unsupported: 'Geolocation not supported',
}
